import { useState, type FormEvent } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useCreateCase } from './use-create-case';

type NewCaseDialogProps = {
  readonly onClose: () => void;
};

type FormState = {
  readonly navn: string;
  readonly saksnummer: string;
  readonly klient: string;
};

const EMPTY_FORM: FormState = { navn: '', saksnummer: '', klient: '' };

export function NewCaseDialog({ onClose }: NewCaseDialogProps) {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const createCase = useCreateCase();
  const navigate = useNavigate();
  const isValid = form.navn.trim() && form.saksnummer.trim() && form.klient.trim();

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!isValid) return;

    const result = await createCase.mutateAsync({
      navn: form.navn.trim(),
      saksnummer: form.saksnummer.trim(),
      klient: form.klient.trim(),
    });
    onClose();
    await navigate({ to: '/cases/$caseId', params: { caseId: result.caseId } });
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/65 px-4">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-md rounded-xl border border-white/[0.1] bg-[#1a1b23] p-5 shadow-2xl"
      >
        <div className="mb-5">
          <h2 className="text-sm font-semibold text-[#ededf0]">Ny sak</h2>
          <p className="mt-1 text-xs text-[#5e6070]">Opprett en sak og bli eier automatisk.</p>
        </div>

        <div className="flex flex-col gap-3">
          <Input
            id="new-case-name"
            label="Saksnavn"
            value={form.navn}
            onChange={(e) => setForm({ ...form, navn: e.target.value })}
            autoFocus
          />
          <Input
            id="new-case-number"
            label="Saksnummer"
            value={form.saksnummer}
            onChange={(e) => setForm({ ...form, saksnummer: e.target.value })}
          />
          <Input
            id="new-case-client"
            label="Klient"
            value={form.klient}
            onChange={(e) => setForm({ ...form, klient: e.target.value })}
          />
        </div>

        {createCase.isError && (
          <p className="mt-4 rounded-md bg-danger/[0.08] px-3 py-2 text-xs text-danger/80">
            Kunne ikke opprette saken. Prøv igjen.
          </p>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Avbryt
          </Button>
          <Button type="submit" disabled={!isValid || createCase.isPending}>
            {createCase.isPending ? 'Oppretter...' : 'Opprett sak'}
          </Button>
        </div>
      </form>
    </div>
  );
}
